import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { type Translate, useTranslation } from "@redotech/redo-i18n";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { settledExchangeLines } from "@redotech/redo-return-core-common/returns/return-totals/return-summary-totals-helpers";
import { getShopperShippingFeeLines } from "@redotech/redo-return-core-common/returns/return-totals/shopper-shipping-fee-lines";
import { isReturnLabelCoveredForShopper } from "@redotech/redo-return-core-common/shipping/return-carrier-option";
import {
  CompensationMethod,
  DraftReturn,
  ReturnTotalCalculations,
} from "@redotech/redo-return-operations-common/draft-return/draft-return";
import {
  compensationMethodHasRepair,
  getCombinedReturnItem,
  getCompensationMethod,
} from "@redotech/redo-return-operations-common/draft-return/util";
import { RepairFeeCollectionStrategy } from "@redotech/redo-return-warranties-common/warranties";
import { Resource } from "@redotech/redo-team-common/localization/resource";
import { capitalizeWords } from "@redotech/string/util";
import { arraysEqualIgnoreOrder } from "@redotech/util/equal";
import { useContext, useEffect, useState } from "react";
import { ReturnContext } from "../../contexts/return";
import { RpcClientContext } from "../../contexts/rpc";
import { CustomerPortalAppSettings } from "../../settings";
import { Subtotal } from "../../types";

function quantizedSubtotal(
  title: string,
  total: number,
  quantity: number,
): Subtotal {
  return {
    title: title + (quantity > 1 ? ` x${quantity}` : ""),
    total: total * quantity,
  };
}

export enum SummaryCardType {
  NORMAL = "normal",
  REPAIR = "repair",
}

/**
 * The fee/bonus amount shown on the review/summary adjustment line.
 *
 * `totalAdjustmentValue` only covers return-flow adjustments; a merchant's
 * manually-applied fee/bonus (the "Adjust fee or bonus" action on a
 * merchant-created return) is tracked separately in `totalMerchantAdjustment`.
 * Both are excluded from the displayed pre-adjustment item value, so they must
 * be summed for the adjustment line to reflect what's actually applied when the
 * return is created. Sign convention is preserved: fees are negative, bonuses
 * positive.
 */
export function getDisplayAdjustmentValue(
  calculations: Pick<
    ReturnTotalCalculations,
    "totalAdjustmentValue" | "totalMerchantAdjustment"
  >,
): number {
  return (
    calculations.totalAdjustmentValue +
    (calculations.totalMerchantAdjustment ?? 0)
  );
}

/**
 * Resolves a "Hide warranty item costs" toggle. The setting only applies to
 * warranty flows and defaults to off, so item and draft-order amounts are only
 * hidden when the return is a warranty AND the merchant enabled the toggle.
 * Page-agnostic: pass the relevant page's stored flag (review or confirmation).
 */
export function resolveHideItemCosts(
  returnType: ReturnTypeEnum | undefined,
  hideWarrantyItemCosts: boolean | null | undefined,
): boolean {
  if (returnType !== ReturnTypeEnum.WARRANTY) {
    return false;
  }
  return hideWarrantyItemCosts ?? false;
}

/**
 * Resolves the merchant-customized "Due later" explanatory text for the review
 * page. Each flow resolves against its own variant (warranty vs return) with no
 * cross-variant fallback, matching the rest of the review-page customization.
 * Returns undefined when unset (or whitespace-only) so the summary card renders
 * its built-in default. The returned string may contain a `%price%` token for
 * the caller to substitute with the formatted amount owed.
 */
export function resolveDueLaterSubtext(
  returnType: ReturnTypeEnum | undefined,
  customization: PortalCustomization | null | undefined,
): string | undefined {
  const reviewText =
    returnType === ReturnTypeEnum.WARRANTY
      ? customization?.pages?.review?.warrantyText
      : customization?.pages?.review?.text;
  return reviewText?.dueLaterSubtext?.trim() || undefined;
}

/**
 * Lines under the return value header after the items. Duties (with their tax)
 * show only when refunded, so the visible lines add up to the refund.
 */
export function buildReturnValueSubtotals(
  calculations: Pick<ReturnTotalCalculations, "totalTaxes" | "totalDuties">,
  t: Translate,
): Subtotal[] {
  const { totalTaxes, totalDuties } = calculations;
  return [
    ...(totalTaxes ? [{ title: t("Taxes"), total: totalTaxes }] : []),
    ...(totalDuties
      ? [
          {
            title: t("Duties", {
              context: "customs import duties refunded on a return",
            }),
            total: totalDuties,
          },
        ]
      : []),
  ];
}

function determineSummaryCardType(
  compensationMethod: CompensationMethod | undefined,
): SummaryCardType {
  if (compensationMethod && compensationMethodHasRepair(compensationMethod)) {
    return SummaryCardType.REPAIR;
  }
  return SummaryCardType.NORMAL;
}

export const useSummaryCardReview = ({
  settings,
  customization,
  draftReturn,
  calculations,
  compensationMethod,
  copyResource,
  alert,
}: {
  settings: CustomerPortalAppSettings | undefined;
  customization?: PortalCustomization | null;
  draftReturn: DraftReturn;
  calculations: ReturnTotalCalculations;
  compensationMethod: CompensationMethod | undefined;
  copyResource: Resource;
  alert: (message: string) => void;
}) => {
  const { t } = useTranslation();
  const localizeFeesHeader = (text: string): string => {
    switch (text) {
      case "Return value":
        return t("Return value");
      case "Claim value":
        return t("Claim value");
      case "Warranty claim value":
        return t("Warranty claim value");
      case "Recycling value":
        return t("Recycling value");
      default:
        return text;
    }
  };

  const localizeFeesOrderHeader = (text: string): string => {
    switch (text) {
      case "New order":
        return t("New order");
      case "Replacement order":
        return t("Replacement order");
      default:
        return text;
    }
  };

  const getLocalizedAdjustmentName = (
    adjustmentName: string | undefined,
    defaultKey: "bonus" | "fee",
  ): string => {
    if (!adjustmentName) {
      return defaultKey === "bonus" ? t("bonus") : t("fee");
    }
    const normalizedName = adjustmentName.trim().toLowerCase();
    if (normalizedName === "bonus") {
      return t("bonus");
    }
    if (normalizedName === "fee") {
      return t("fee");
    }
    return adjustmentName;
  };
  const isMerchant = draftReturn.merchantCreated ?? false;
  const returnContext = useContext(ReturnContext);
  const client = useRequiredContext(RpcClientContext);
  const [discountCodes, setDiscountCodes] = useState<string[]>([]);
  const [newDiscountCode, setNewDiscountCode] = useState<string>("");

  useEffect(() => {
    setDiscountCodes(draftReturn.newOrderData?.discountCodes ?? []);
  }, [draftReturn]);

  const [addDiscountCodesLoad, doAddDiscountCodes] = useTriggerLoad(
    async (signal) => {
      await addCustomerDiscountCodes(signal);
    },
  );

  const addCustomerDiscountCodes = useHandler(async (signal: AbortSignal) => {
    if (!returnContext.draftReturn) {
      throw new Error("No draft return found");
    }
    const { draftReturn: updatedDraftReturn } =
      await client.addCustomerDiscountCodes(
        { draftReturnId: returnContext.draftReturn._id, discountCodes },
        { signal },
      );

    returnContext.setDraftReturn(updatedDraftReturn);
    if (
      !arraysEqualIgnoreOrder(
        discountCodes,
        updatedDraftReturn.newOrderData?.discountCodes ?? [],
      )
    ) {
      const missingDiscountCodes = discountCodes.filter(
        (code) =>
          !(updatedDraftReturn.newOrderData?.discountCodes ?? [])
            .map((existingCode) => existingCode.toLowerCase())
            .includes(code.toLowerCase()),
      );
      alert(
        `Unable to apply discount codes: ${missingDiscountCodes.join(", ")}`,
      );
    }
  });

  const {
    totalRefund,
    totalStoreCredit,
    shippingFee,
    totalShippingFee,
    pickupFee,
    carrierUpgradeFeeComponent,
    returnValue,
    newOrderValue,
    totalNewOrderTaxes,
    totalPaymentOwed,
    labelDeducted,
    totalRepairFee,
    totalLoyaltyPointsDeducted,
    totalLoyaltyPointsRefunded,
    preDiscountShopNowBonus,
    combinedUnusedUseItOrLoseItBonuses,
  } = calculations;

  const presentmentCurrency =
    draftReturn.presentmentCurrency ?? draftReturn.currency ?? Currency.USD;

  const labelCompensationMethod = getCompensationMethod(draftReturn);
  const shippingFeeLines = getShopperShippingFeeLines({
    amount: shippingFee + (carrierUpgradeFeeComponent ?? 0),
    shopperShippingFee: shippingFee + (carrierUpgradeFeeComponent ?? 0),
    carrierUpgradeFee: carrierUpgradeFeeComponent ?? 0,
    isLabelCovered: isReturnLabelCoveredForShopper({
      labelPayer: labelCompensationMethod?.payer,
      labelPayerReason: labelCompensationMethod?.reason,
    }),
  });

  // Get exchange shipping fee from selected option
  const selectedExchangeShippingOption =
    draftReturn.selectedExchangeShippingOption;
  const exchangeShippingFee = selectedExchangeShippingOption?.priceSet
    ? parseFloat(
        selectedExchangeShippingOption.priceSet.presentmentMoney.amount,
      )
    : selectedExchangeShippingOption?.price;
  const exchangeShippingName = selectedExchangeShippingOption?.name || "";

  const adjustmentValue = getDisplayAdjustmentValue(calculations);

  const taxSubtotals = buildReturnValueSubtotals(calculations, t);

  const pointsDeductedFromRefundSubtotal: Subtotal | undefined =
    calculations.totalReturnValueDeductedDueToLoyaltyPoints
      ? {
          title: t("Loyalty Points Deducted"),
          total: -calculations.totalReturnValueDeductedDueToLoyaltyPoints,
        }
      : undefined;

  const productSubtotals: Subtotal[] = draftReturn.pendingReturnItems.flatMap(
    (pendingReturnItem) => {
      const combinedItem = getCombinedReturnItem(
        draftReturn,
        pendingReturnItem.id,
      );

      return combinedItem.returnableItem.products.reduce<Subtotal[]>(
        (acc, product) => {
          if (
            combinedItem.returnableItem.isExpanded &&
            !product?.isUnbundled &&
            !product?.isExpandedPrimaryProduct
          ) {
            return acc;
          }
          const quantity = pendingReturnItem.quantity ?? 1;
          acc.push(
            quantizedSubtotal(product.title, +product.returnValue, quantity),
          );
          return acc;
        },
        [],
      );
    },
  );

  const newOrderDiscount = calculations.newOrderDiscount ?? 0;
  const newOrderDiscountSubtotals: Subtotal[] =
    newOrderDiscount > 0
      ? [{ title: t("Discount"), total: -newOrderDiscount }]
      : [];

  const newOrderTaxes: Subtotal[] = totalNewOrderTaxes
    ? [{ title: t("Taxes"), total: totalNewOrderTaxes }]
    : [];

  const newVariantExchangeItemSubtotals: Subtotal[] = Object.entries(
    compensationMethod?.itemValues || {},
  )
    .flatMap(([id, item]) => {
      if (!item.isVariantExchange) {
        return null;
      }
      const combinedItem = getCombinedReturnItem(draftReturn, id);
      const quantity = combinedItem.pendingReturnItem?.quantity ?? 1;
      return combinedItem.returnableItem.products.map((product) =>
        quantizedSubtotal(product.title, +product.price, quantity),
      );
    })
    .filter((item) => item != null);

  // A line the engine settled is priced by the calculator, at the units it
  // settled against; the document's own price does not know the pairing. The
  // calculator's figure is the whole line, so it replaces the total as is.
  const newOrderItemsSubtotals: Subtotal[] = (
    draftReturn?.newOrderData?.items || []
  ).map((item, index) => {
    const line = quantizedSubtotal(
      item.title,
      parseFloat(item.priceSet?.presentmentMoney?.amount ?? item.price),
      item.quantity,
    );
    const settled = settledExchangeLines(calculations.exchangeItemTotals).find(
      (candidate) => candidate.id === `${item.variantId}-${index}`,
    );
    return settled ? { ...line, total: settled.price } : line;
  });

  const repairFeeCollectionStrategy =
    draftReturn.repairFeeCollectionStrategy ??
    RepairFeeCollectionStrategy.DEFERRED;

  const isPending = addDiscountCodesLoad.pending;

  const showDiscountCodes =
    customization?.pages?.review?.behavior?.showDiscountCodes ??
    !settings?.theme.hideDiscountCodes;

  // Warranty-only review setting: hide item and draft order amounts while
  // still surfacing return shipping costs. Only applies to warranty flows.
  const hideItemCosts = resolveHideItemCosts(
    draftReturn.returnType,
    customization?.pages?.review?.behavior?.hideWarrantyItemCosts,
  );

  // Merchant-customizable "Due later" explanatory text. Empty → undefined so
  // the summary card renders its built-in default. Supports a `%price%` token
  // that the summary card replaces with the formatted amount owed.
  const dueLaterSubtext = resolveDueLaterSubtext(
    draftReturn.returnType,
    customization,
  );

  return {
    adjustmentValue,
    adjustmentValueString: capitalizeWords(
      adjustmentValue > 0
        ? getLocalizedAdjustmentName(
            customization?.pages?.review?.text?.adjustmentPositiveName ||
              settings?.returnAdjustment?.positiveName,
            "bonus",
          )
        : getLocalizedAdjustmentName(
            customization?.pages?.review?.text?.adjustmentNegativeName ||
              settings?.returnAdjustment?.negativeName,
            "fee",
          ),
    ),
    currency: presentmentCurrency,
    feesHeader: localizeFeesHeader(copyResource.FEES_HEADER),
    feesOrderHeader: localizeFeesOrderHeader(copyResource.FEES_ORDER_HEADER),
    repairFeeCollectionStrategy,
    labelDeducted,
    newOrderDiscountSubtotals,
    newOrderItemsSubtotals,
    newOrderTaxes,
    newOrderValue,
    newVariantExchangeItemSubtotals,
    totalPaymentOwed,
    pickupFee,
    productSubtotals,
    returnValue,
    shippingFee: shippingFeeLines.returnShipping,
    totalShippingFee,
    carrierUpgradeFeeComponent: shippingFeeLines.carrierUpgrade,
    exchangeShippingFee,
    exchangeShippingName,
    taxSubtotals,
    pointsDeductedFromRefundSubtotal,
    totalRefund,
    totalStoreCredit,
    totalLoyaltyPointsDeducted,
    totalLoyaltyPointsRefunded,
    preDiscountShopNowBonus,
    combinedUnusedUseItOrLoseItBonuses,
    summaryCardType: determineSummaryCardType(compensationMethod),
    hideItemCosts,
    dueLaterSubtext,
    totalRepairFee,
    customerDiscountInputs:
      !isMerchant &&
      !!draftReturn.newOrderData &&
      settings?.exchanges.allowCustomerDiscountCodes &&
      showDiscountCodes
        ? {
            texts: {
              discountInputLabel: t("Discount codes"),
              discountInputDescription: t(
                "These discounts will be applied to this order",
              ),
            },
            inputs: {
              enabled: !isPending,
              discountCodes,
              onChangeDiscountCodes: (discountCodes: string[]) => {
                setDiscountCodes(discountCodes);
                doAddDiscountCodes();
              },
              newDiscountCode,
              onChangeNewDiscountCode: setNewDiscountCode,
            },
          }
        : undefined,
  };
};
